<template>
    <component
        :is="hasTooltip ? 'vx-tooltip' : 'div'"
        :color="dynamicPopoverColor"
        :text="tooltip"
        :position="tooltipPosition"
        delay=".1s"
        class="vx-action-icon"
    >
        <template slot="content">
            <slot name="tooltip"></slot>
        </template>
        <vx-button
            v-if="$slots['custom']"
            v-on="$listeners"
            class="custom-action-button"
            icon-pack="feather"
            style="height: 38px !important; width: 38px !important"
            :class="[{ disabled: disabled }, { active: active }, { grow: growWhenActive }]"
            :color="dynamicColor"
            :radius="rounded"
            :disabled="disabled"
            @mouseenter="mouseEnter"
            @mouseleave="mouseLeave"
        >
            <slot name="custom"></slot>
        </vx-button>
        <vx-button
            v-else-if="isButton"
            v-on="$listeners"
            class="custom-action-button"
            :icon-pack="useMdi ? 'material-icons' : 'feather'"
            :class="[{ disabled: disabled }, { active: active }, { grow: growWhenActive }]"
            :icon="dynamicIcon"
            :color="dynamicColor"
            :radius="rounded"
            :disabled="disabled"
            @mouseenter="mouseEnter"
            @mouseleave="mouseLeave"
        >
        </vx-button>
        <vx-basic-icon
            v-else
            v-on="$listeners"
            :use-mdi="useMdi"
            :icon="dynamicIcon"
            class="custom-action-icon"
            :class="[{ disabled: disabled }, { active: active }, { grow: growWhenActive }]"
            :size="size"
            :filled="filled"
            :rounded="rounded"
            :color="dynamicColor"
            :outlined="outlined"
            @mouseenter="mouseEnter"
            @mouseleave="mouseLeave"
            @mousedown.stop.prevent
            @mouseup.stop.prevent
        />
    </component>
</template>

<script>
export default {
    name: 'vx-action-icon',
    props: {
        useMdi: {
            type: Boolean,
            default: false
        },
        tooltip: {
            type: String
        },
        tooltipPosition: {
            type: String,
            default: 'top'
        },
        icon: {
            type: String,
            required: true
        },
        hoverIcon: {
            type: String
        },
        activeIcon: {
            type: String
        },
        disabled: {
            type: Boolean,
            default: false
        },
        rounded: {
            type: Boolean,
            default: false
        },
        size: {
            type: [Number, String],
            default: 6
        },
        active: {
            type: Boolean,
            default: false
        },
        color: {
            type: String,
            default: 'dark'
        },
        hoverColor: {
            type: String
        },
        activeColor: {
            type: String
        },
        growWhenActive: {
            type: Boolean,
            default: false
        },
        filled: {
            type: Boolean,
            default: false
        },
        outlined: {
            type: Boolean,
            default: false
        },
        isButton: {
            type: Boolean,
            default: false
        },
        isLoading: {
            type: Boolean,
            default: false
        }
    },
    watch: {
        icon: {
            handler: function (v) {
                this.parsedIcon = this.isButton ? this.parseButtonIcon(v) : this.parseIcon(v)
            },
            immediate: true
        },
        hoverIcon: {
            handler: function (v) {
                this.parsedHoverIcon = this.isButton ? this.parseButtonIcon(v) : this.parseIcon(v)
            },
            immediate: true
        },
        activeIcon: {
            handler: function (v) {
                this.parsedActiveIcon = this.isButton ? this.parseButtonIcon(v) : this.parseIcon(v)
            },
            immediate: true
        }
    },
    data() {
        return {
            hovered: false,
            parsedHoverIcon: '',
            parsedActiveIcon: '',
            parsedIcon: ''
        }
    },
    computed: {
        hasTooltip() {
            return this.tooltip || this.$slots['tooltip']
        },
        dynamicIcon() {
            if (this.hovered && this.hoverIcon) {
                return this.parsedHoverIcon
            } else if (this.active && this.activeIcon) {
                return this.parsedActiveIcon
            }
            return this.parsedIcon
        },
        dynamicColor() {
            if (this.hovered && this.hoverColor) {
                return this.hoverColor
            } else if (this.active && this.activeColor) {
                return this.activeColor
            }
            return this.color
        },
        dynamicPopoverColor() {
            if (this.active && this.activeColor) {
                return this.activeColor
            }
            return this.hoverColor || this.color
        }
    },
    methods: {
        mouseEnter(e) {
            this.hovered = true
        },
        mouseLeave() {
            this.hovered = false
        },
        parseIcon(iconName) {
            if (!iconName) {
                return ''
            }
            if (!this.useMdi) {
                var pascalIcon = iconName.toPascalCase()
                if (pascalIcon.startsWith('Icon')) {
                    var parsedIconName = pascalIcon.substring(4, pascalIcon.length)
                    return `${parsedIconName}Icon`
                }
                return pascalIcon
            }
            return iconName
        },
        parseButtonIcon(iconName) {
            if (!iconName) {
                return ''
            }
            if (this.useMdi) {
                return iconName
            }
            var kebabIcon = iconName.toKebabCase()
            if (kebabIcon.endsWith('-icon')) {
                kebabIcon = `icon-${kebabIcon.substring(0, kebabIcon.length - 5)}`
            }

            if (!kebabIcon.startsWith('icon-')) {
                return `icon-${kebabIcon}`
            }

            return kebabIcon
        }
    }
}
</script>

<style scoped lang="scss">
.vx-action-icon {
    // gets rid of invisible height from hidden text???
    font-size: 0.1rem;
    user-select: none;
}
.feather-icon >>> svg {
    padding: 0;
    transition-delay: 0s;
    transition-duration: 0.25s;
    transition-property: all;
    transition-timing-function: ease-out;
    stroke-width: 2px !important;
    user-select: none;
}
.tooltip {
    height: 22px !important;
    transition: all 0.3s ease;
}

.custom-action-button {
    transition: all 0.2s ease;
    ::v-deep .vs-icon {
        transform: scale(1.5);
    }
    &.active.grow {
        ::v-deep .vs-icon {
            transform: scale(1.8);
        }

        &:hover {
            ::v-deep .vs-icon {
                transform: scale(1.5);
            }
        }
    }
    &.disabled {
        opacity: 0.3;
        pointer-events: none;
    }
    &:hover {
        ::v-deep .vs-icon {
            transform: scale(1.3);
        }
    }
}

.custom-action-icon {
    cursor: pointer;
    transition: transform 0.2s ease;
    transform: scale(1.2);

    &.disabled {
        opacity: 0.3;
        pointer-events: none;
    }
    &:hover {
        transform: scale(1.4);
    }
    &.active.grow {
        transform: scale(1.8);

        &:hover {
            transform: scale(1.6);
        }
    }
}
</style>
